import { BookOpen, ChevronDown, ExternalLink, Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/empty-state";
import { GuideNotesEditor } from "@/components/guides/GuideNotesEditor";
import { SearchInput } from "@/components/shared/SearchInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuideCategories, useGuideEntries } from "@/hooks/api/guides";
import { resolveBilingual, type ResolvedText } from "@/lib/bilingual";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import type { GuideEntry, LanguagePreference } from "@/types/api";

const ALL = "all";

/** Same compact bilingual treatment as GuideListPage/FlowPreviewPanel — both
 * languages shown directly, never hidden behind a hover, since this can be
 * read live mid-call. */
function PanelBilingualBlock({ text, className }: { text: ResolvedText; className: string }) {
  if (!text.secondary) {
    return <p className={className}>{text.primary}</p>;
  }
  return (
    <div className="space-y-2">
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          EN
        </span>
        <p className={className}>{text.primary}</p>
      </div>
      <div className="border-t border-slate-200 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          ES
        </span>
        <p className={className}>{text.secondary}</p>
      </div>
    </div>
  );
}

/**
 * Always-on Guides/FAQ reference shown alongside the flow player (see
 * FlowPlayerPage). Same content and search/filter as the full GuideListPage,
 * condensed for a sidebar, so Staff can look up off-script guidance —
 * difficult callers, edge cases — without leaving the script they're
 * running mid-call. "Open full guides page" is the escape hatch for when a
 * card needs more room than the sidebar gives it.
 */
export function GuidePanel() {
  const { t } = useTranslation();
  const { data: entriesData, isLoading, error } = useGuideEntries();
  const { data: categoriesData } = useGuideCategories();
  const languagePreference = useAuthStore((s) => s.user?.language_preference) ?? "BOTH";

  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string>(ALL);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const entries = entriesData?.results ?? [];

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      const matchesCategory = categoryId === ALL || entry.category.id === categoryId;
      const q = query.trim().toLowerCase();
      const matchesQuery =
        q === "" ||
        [
          entry.title,
          entry.title_es,
          entry.spiel_text,
          entry.spiel_text_es,
          entry.internal_note,
          entry.internal_note_es,
        ].some((field) => field.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [entries, categoryId, query]);

  const categories = categoriesData?.results ?? [];
  const categoriesWithEntries = categories.filter((cat) =>
    entries.some((e) => e.category.id === cat.id)
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <BookOpen className="h-4 w-4 text-primary" aria-hidden="true" />
          {t("guideList.title")}
        </div>
        <Link
          to="/guides"
          className="flex shrink-0 items-center gap-1 text-xs text-slate-500 hover:text-primary hover:underline"
        >
          {t("guidePanel.openFullPage")}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>

      <div className="shrink-0 space-y-2 border-b border-slate-200 bg-surface-alt px-4 py-3">
        <SearchInput
          placeholder={t("guideList.searchPlaceholder")}
          onSearch={setQuery}
          debounceDelay={200}
          size="sm"
        />
        {categoriesWithEntries.length > 0 && (
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger
              className="h-8 w-full text-xs"
              aria-label={t("guidePanel.categoryFilterLabel")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("guideList.allCategories")}</SelectItem>
              {categoriesWithEntries.map((cat) => {
                const label = resolveBilingual(languagePreference, {
                  en: cat.name,
                  es: cat.name_es,
                });
                return (
                  <SelectItem key={cat.id} value={cat.id}>
                    {label.primary}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {error && <p className="text-sm text-danger">{t("guideList.loadError")}</p>}

        {!isLoading && !error && entries.length === 0 && (
          <EmptyState
            className="py-6"
            icon={BookOpen}
            title={t("guideList.noEntriesTitle")}
            description={t("guideList.noEntriesDescription")}
          />
        )}

        {!isLoading && !error && entries.length > 0 && filtered.length === 0 && (
          <EmptyState
            className="py-6"
            icon={BookOpen}
            title={t("guideList.noMatchesTitle")}
            description={t("guideList.noMatchesDescription")}
          />
        )}

        {filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((entry) => (
              <PanelGuideCard
                key={entry.id}
                entry={entry}
                languagePreference={languagePreference}
                expanded={expandedId === entry.id}
                onToggle={() =>
                  setExpandedId((current) => (current === entry.id ? null : entry.id))
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PanelGuideCard({
  entry,
  languagePreference,
  expanded,
  onToggle,
}: {
  entry: GuideEntry;
  languagePreference: LanguagePreference;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const title = resolveBilingual(languagePreference, { en: entry.title, es: entry.title_es });
  const spiel = entry.spiel_text.trim()
    ? resolveBilingual(languagePreference, { en: entry.spiel_text, es: entry.spiel_text_es })
    : null;
  const note = entry.internal_note.trim()
    ? resolveBilingual(languagePreference, {
        en: entry.internal_note,
        es: entry.internal_note_es,
      })
    : null;

  return (
    <div className="app-card overflow-hidden p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="app-focus-ring flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <div className="min-w-0">
          <div className="text-[10px] font-medium uppercase tracking-wide text-primary">
            {entry.category.name}
          </div>
          <div className="truncate text-sm font-semibold text-slate-900">{title.primary}</div>
          {title.secondary && (
            <div className="truncate text-xs text-slate-500">{title.secondary}</div>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150",
            expanded && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-3">
          {spiel && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                {t("flowPlayer.sayThis")}
              </div>
              <div className="mt-1">
                <PanelBilingualBlock
                  text={spiel}
                  className="text-sm leading-relaxed text-slate-900"
                />
              </div>
            </div>
          )}
          {note && (
            <div className="flex gap-2 rounded-md border border-warning/30 bg-warning-subtle p-2.5 text-xs text-slate-800">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
              <div>
                <div className="font-medium text-warning">{t("guideList.guidanceLabel")}</div>
                <PanelBilingualBlock text={note} className="mt-0.5 leading-relaxed" />
              </div>
            </div>
          )}
          {!spiel && !note && (
            <p className="text-sm text-slate-500">{t("guideList.noContent")}</p>
          )}
          <GuideNotesEditor entryId={entry.id} compact />
        </div>
      )}
    </div>
  );
}
